import { useEffect, useRef, type ClipboardEvent, type KeyboardEvent } from "react";
import { cn } from "@/lib/utils";

interface OtpCodeInputProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  length?: number;
  disabled?: boolean;
  invalid?: boolean;
  autoFocus?: boolean;
  className?: string;
  inputClassName?: string;
}

export function OtpCodeInput({
  id,
  value,
  onChange,
  length = 6,
  disabled = false,
  invalid = false,
  autoFocus = false,
  className,
  inputClassName,
}: OtpCodeInputProps) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length }, (_, index) => value[index] ?? "");

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  function commit(nextDigits: string[]) {
    onChange(nextDigits.join("").replace(/\D/g, "").slice(0, length));
  }

  function setDigit(index: number, rawValue: string) {
    const clean = rawValue.replace(/\D/g, "");
    if (!clean) {
      const next = [...digits];
      next[index] = "";
      commit(next);
      return;
    }

    const next = [...digits];
    for (let offset = 0; offset < clean.length && index + offset < length; offset++) {
      next[index + offset] = clean[offset];
    }
    commit(next);
    refs.current[Math.min(index + clean.length, length - 1)]?.focus();
  }

  function onKeyDown(index: number, event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      refs.current[index - 1]?.focus();
      return;
    }
    if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault();
      refs.current[index - 1]?.focus();
      return;
    }
    if (event.key === "ArrowRight" && index < length - 1) {
      event.preventDefault();
      refs.current[index + 1]?.focus();
    }
  }

  function onPaste(event: ClipboardEvent<HTMLDivElement>) {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (!pasted) return;
    event.preventDefault();
    onChange(pasted);
    refs.current[Math.min(pasted.length, length - 1)]?.focus();
  }

  return (
    <div
      id={id}
      role="group"
      aria-invalid={invalid || undefined}
      className={cn("grid grid-cols-6 gap-2 sm:gap-2.5", className)}
      onPaste={onPaste}
    >
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(element) => {
            refs.current[index] = element;
          }}
          value={digit}
          inputMode="numeric"
          maxLength={length}
          autoComplete={index === 0 ? "one-time-code" : undefined}
          aria-label={`Digit ${index + 1}`}
          disabled={disabled}
          onChange={(event) => setDigit(index, event.target.value)}
          onKeyDown={(event) => onKeyDown(index, event)}
          className={cn(
            "aspect-square h-12 min-h-0 w-full min-w-0 rounded-xl border-[1.5px] bg-card text-center text-xl font-semibold text-foreground shadow-sm outline-none transition md:h-13",
            "focus:border-primary focus:ring-[3px] focus:ring-primary/15",
            "disabled:cursor-not-allowed disabled:opacity-50",
            digit ? "border-primary/55 bg-primary/[0.03]" : "border-border",
            invalid && "border-destructive bg-destructive/[0.03] focus:border-destructive focus:ring-destructive/15",
            inputClassName,
          )}
          style={{ fontFamily: "var(--font-heading)" }}
        />
      ))}
    </div>
  );
}
